import React, { useState } from 'react';
import { MapPin, Mail, Phone, Award } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { contactInfo } from '../data/mockData';
import { useToast } from '../hooks/use-toast';

export const ContactUs = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    toast({
      title: "Inquiry Submitted",
      description: "Thank you for your inquiry. We will contact you soon.",
    });
    setFormData({
      name: '',
      organization: '',
      email: '',
      phone: '',
      message: ''
    });
  };

  return (
    <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-gray-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1e3a8a] mb-4">
            Contact Us
          </h2>
          <p className="text-lg text-gray-600">Get in touch with our team for your procurement needs</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-6">
            <Card className="p-6 bg-white border-gray-200 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#1e3a8a] bg-opacity-10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-[#1e3a8a]" size={24} />
                </div>
                <div>
                  <h4 className="text-gray-800 font-semibold mb-1">Location</h4>
                  <p className="text-gray-600">{contactInfo.location}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-white border-gray-200 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#1e3a8a] bg-opacity-10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="text-[#1e3a8a]" size={24} />
                </div>
                <div>
                  <h4 className="text-gray-800 font-semibold mb-1">Email</h4>
                  <p className="text-gray-600">{contactInfo.email}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-white border-gray-200 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#1e3a8a] bg-opacity-10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="text-[#1e3a8a]" size={24} />
                </div>
                <div>
                  <h4 className="text-gray-800 font-semibold mb-1">Phone</h4>
                  <p className="text-gray-600">{contactInfo.phone}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-teal-50 border-teal-200">
              <div className="flex items-center space-x-3">
                <Award className="text-teal-700" size={24} />
                <p className="text-teal-900 font-semibold">Authorized Seller on GeM</p>
              </div>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="p-8 bg-white border-gray-200 shadow-lg">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-gray-700">
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="organization" className="text-gray-700">
                  Organization <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="organization"
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                  required
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-gray-700">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-gray-700">
                  Phone <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-gray-700">
                  Message <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="mt-1.5"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#1e3a8a] hover:bg-[#1e40af] text-white py-6 text-lg rounded-lg transition-colors duration-200"
              >
                Submit Inquiry
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
};